import { useMemo } from 'react';
import { UnitSystem } from '~/_shared/types/googleMaps/googleMaps.types';
import { convertDistanceToUnitSystem } from '~/_shared/utils/distance/distance.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { notNull } from '~/_shared/utils/typeGuards';
import type { ReportDemographicData } from '~/exportReport/exportReportData.helpers';
import { ExportReportType } from '~/exportReport/exportReportType';
import type { ReportTableColumn } from '~/exportReport/table/ReportTable.model';
import { ExportReportTemplateRendererComponent } from '~/exportReport/templateRenderer/exportReportTemplateRenderer.component';
import { useLatLngSpreadsheetData } from '~/map/map/useSpreadsheetData.hook';
import { useToolsStateExportReportSelector } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.selectors';

type ExportReportContentProps = Readonly<{
  tableColumns: ReadonlyArray<ReportTableColumn>;
  getDemographicLabel: (demographicId: string) => string | null;
  getDemographicData: (columnId: string, demographicId: string) => ReportDemographicData | null;
  isLoading?: boolean;
}>;

// Renders the active export report (or its sample) for both the preview modal and the print page.
export const ExportReportContentContainer = ({ tableColumns, getDemographicLabel, getDemographicData, isLoading }: ExportReportContentProps) => {
  const exportReportState = useToolsStateExportReportSelector();
  const spreadsheetData = useLatLngSpreadsheetData();
  const [t] = useTranslation();

  const radiusInMetersList = useMemo(() => tableColumns.map(column => column.type === 'radius' ? convertDistanceToUnitSystem({
    distance: column.distance * 1000,
    currentUnitSystem: column.unit,
    targetUnitSystem: UnitSystem.metric,
  }) : null).filter(notNull), [tableColumns]);

  if (!exportReportState.isActive) {
    return null;
  }

  const { sample, template } = exportReportState.settings;
  // Sample reports are drawn like the opened report, but around a predefined location.
  const params = sample?.visualParams ?? exportReportState.params;

  const commonProps = {
    template,
    dataSourceColumns: tableColumns,
    getDemographicLabel,
    getDemographicData,
    isLoading,
    isSampleReport: !!sample,
  };

  switch (params.type) {
    case ExportReportType.Location: {
      const center = 'markerId' in params.data
        ? spreadsheetData.getRow(params.data.markerId)
        : params.data.center;

      if (!center) {
        return null;
      }

      return (
        <ExportReportTemplateRendererComponent
          {...commonProps}
          summaryProps={{
            latLng: { lat: center.lat, lng: center.lng },
            heading: params.data.summary.heading,
            semiHeading: params.data.summary.semiHeading,
            description: params.data.summary.description,
          }}
          mapProps={{
            type: ExportReportType.Location,
            center,
          }}
          roadmapProps={{
            radiusInMeters: radiusInMetersList,
          }}
        />
      );
    }

    case ExportReportType.Radius:
      return (
        <ExportReportTemplateRendererComponent
          {...commonProps}
          summaryProps={{
            latLng: params.data.center,
            heading: params.data.title,
            semiHeading: params.data.semiHeading,
          }}
          mapProps={{
            type: ExportReportType.Radius,
            center: params.data.center,
            radiusInMeters: convertDistanceToUnitSystem({
              distance: params.data.radius,
              currentUnitSystem: params.data.unit,
              targetUnitSystem: UnitSystem.metric,
            }) * 1000,
          }}
        />
      );

    case ExportReportType.DriveTimePolygon:
      return (
        <ExportReportTemplateRendererComponent
          {...commonProps}
          summaryProps={{
            heading: t('Drive Time Polygon'),
          }}
          mapProps={{
            type: params.type,
            paths: params.data.paths,
            center: params.data.center,
          }}
        />
      );

    case ExportReportType.Polygon:
      return (
        <ExportReportTemplateRendererComponent
          {...commonProps}
          summaryProps={{
            heading: params.data.heading,
          }}
          mapProps={{
            type: params.type,
            paths: params.data.paths,
          }}
        />
      );

    default:
      return null;
  }
};
